/**
 * Based on https://github.com/contentco/quill-snow-sticky-toolbar with some modifications
 */
import {WindowService} from '../../../../../../../shared/services/window';

/**
 * Module for the iOS Safari specific fixes we need. Necessary for the lack of viewport updates on iOS Safari when the keyboard is visible
 * @class
 */
export class IOSSafariQuillEditorModule {

  /**
   * Editor class for when it has focus
   * @protected
   * @type {string}
   */
  protected editorHasFocusClass: string = 'editor-has-focus';

  /**
   * Editor class for regular iPhones
   * @protected
   * @type {string}
   */
  protected iosEditorClass: string = 'ios-keyboard-has-focus-quill-editor';

  /**
   * Editor class for iPhone pluses
   * @protected
   * @type {string}
   */
  protected iosEditorClassPlus: string = 'ios-keyboard-has-focus-quill-editor-plus';

  /**
   * Editor class for iPhone X
   * @protected
   * @type {string}
   */
  protected iosEditorClassX: string = 'ios-keyboard-has-focus-quill-editor-x';

  /**
   * Body class for regular iPhones
   * @protected
   * @type {string}
   */
  protected iosBodyClass: string = 'ios-keyboard-has-focus-body-container';

  /**
   * Body class for iPhone pluses
   * @protected
   * @type {string}
   */
  protected iosBodyClassPlus: string = 'ios-keyboard-has-focus-body-container-plus';

  /**
   * Body class for iPhone X
   * @protected
   * @type {string}
   */
  protected iosBodyClassX: string = 'ios-keyboard-has-focus-body-container-x';

  /**
   * Instance of the Quill class
   * @type {Quill}
   */
  protected quill: any;

  /**
   * Instance of the quill toolbar module
   * @type {Quill.Toolbar}
   */
  protected toolbar: any;

  /**
   * Editor element
   * @protected
   * @type {any}
   */
  protected editor: any;

  /**
   * Editor parent element
   * @protected
   * @type {any}
   */
  protected editorParent: any;

  /**
   * Toolbar container
   * @protected
   * @type {any}
   */
  protected toolbarContainer: any;

  /**
   * Quill root
   * @protected
   * @type {any}
   */
  protected root: any;

  /**
   * Instance of the WindowService
   * @protected
   * @type {WindowService}
   */
  protected windowService: WindowService;

  /**
   * @constructor
   * @param {Quill} quill Instance of Quill
   * @param {{}} props Quill Properties
   */
  constructor(quill, props) {
    this.quill = quill;
    this.toolbar = quill.getModule('toolbar');
    this.toolbarContainer = this.toolbar.container;
    this.editor = this.toolbarContainer.parentNode;
    this.editorParent = this.editor.parentNode;
    this.root = this.quill.root;
    this.windowService = new WindowService();

    this.root.on('focusin.quillIosSafari', () => this.hFocusIn());
    this.root.on('focusout.quillIosSafari', () => this.hFocusOut());
    this.toolbarContainer.on('mousedown', (e) => e.preventDefault());
  }

  /**
   * Handles the focus in event for ios devices
   * @returns {void}
   */
  hFocusIn(): void {
    const editorClass = this.getEditorClass(),
      bodyClass = this.getBodyClass();

    this.editor.classList.add(this.editorHasFocusClass);

    if (!this.quill.hasFocus() || !this.isIphone()) {
      return;
    }

    this.editor.classList.add(editorClass);
    this.editorParent.classList.add(bodyClass);
    this.root.focus();
    setTimeout(() => {
      this.windowService.window.scrollTo(0, 0);
      const selection = document.getSelection();
      if (selection.rangeCount > 0) {
        const range = selection.getRangeAt(0);
        let element: any = range.startContainer;
        if (!(element instanceof Element)) {
          element = range.startContainer.parentElement;
        }

        const clientRects = element.getClientRects();
        let top = 0;
        if (clientRects.length > 0) {
          top = clientRects[0].top;
        }

        const quillEditor = element.closest('.ql-editor');
        if (top > 58) {
          setTimeout(() => {
            quillEditor && quillEditor.scrollTo(0, top - 56);
            const rangeClone = range.cloneRange();
            rangeClone.collapse(true);
            selection.removeAllRanges();
            selection.addRange(rangeClone);
          }, 100);
        }
      }
    }, 100);
  }

  /**
   * Handles the focusout event for ios devices
   * @returns {void}
   */
  hFocusOut(): void {
    const editorClass = this.getEditorClass(),
      bodyClass = this.getBodyClass();

    this.editor.classList.remove(this.editorHasFocusClass);
    if (this.quill.hasFocus() || !this.isIphone()) {
      return;
    }

    this.editor.classList.remove(editorClass);
    this.editorParent.classList.remove(bodyClass);
  }

  /**
   * Gets the editor class for ios based on device type
   * @returns {string|null}
   */
  getEditorClass(): string | null {

    switch (this.iPhoneVersion()) {
      case 'reg':
        return this.iosEditorClass;
      case 'plus':
        return this.iosEditorClassPlus;
      case 'x':
        return this.iosEditorClassX;
      default:
        return null;
    }
  }

  /**
   * Gets the body class for ios based on device type
   * @returns {string|null}
   */
  getBodyClass(): string | null {
    switch (this.iPhoneVersion()) {
      case 'reg':
        return this.iosBodyClass;
      case 'plus':
        return this.iosBodyClassPlus;
      case 'x':
        return this.iosBodyClassX;
      default:
        return null;
    }
  }

  /**
   * Gets the iphone version based on height and width
   * @returns {string}
   */
  iPhoneVersion(): string {
    const iHeight = this.windowService.window.screen.height,
      iWidth = this.windowService.window.screen.width;

    if (iWidth === 375 && iHeight === 667) {
      return 'reg';
    }
    else if (iWidth === 414 && iHeight === 736) {
      return 'plus';
    }
    else if (iWidth === 375 && iHeight === 812) {
      return 'x';
    }
    return 'none';
  }

  /**
   * Checks to see if the device is an iPhone
   * @returns {boolean}
   */
  isIphone(): boolean {
    return !!navigator.userAgent.match(/iPhone/i);
  }
}



// WEBPACK FOOTER //
// ./src/app/content/shared/components/inputs/body/quill/modules/ios-editor.quill.module.ts